<template>
  <div class="home" :class="{ 'home--dashboard': showCapacityDashboard }">
    <div v-if="!showCapacityDashboard" class="home-welcome">
      <h1>{{ homeTitle }}</h1>
    </div>

    <template v-else>
      <header class="home-dash__hero">
        <div class="home-dash__text">
          <h1 class="home-dash__title">{{ homeTitle }}</h1>
          <p class="home-dash__desc">产能、工作量与今日预警一览</p>
        </div>
      </header>

      <nav class="home-dash__nav">
        <button
          v-for="tab in capacityTabs"
          :key="tab.name"
          type="button"
          class="home-dash__nav-item"
          :class="{ 'is-active': activeName === tab.name }"
          @click="switchTab(tab.name)"
        >
          <i :class="tab.icon" class="home-dash__nav-icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      </nav>

      <div v-if="currentTab" class="home-dash__panel-head">
        <div>
          <h2 class="home-dash__panel-title">{{ currentTab.label }}</h2>
          <p class="home-dash__panel-desc">{{ currentTab.desc }}</p>
        </div>
        <el-button
          size="small"
          icon="el-icon-refresh"
          :loading="isRefreshing"
          @click="refreshCurrentTab"
        >
          刷新
        </el-button>
      </div>

      <section class="home-dash__body">
        <keep-alive :include="capacityComponentNames">
          <component
            :is="currentTab.component"
            v-if="currentTab"
            ref="activePanel"
            :key="currentTab.componentName"
          />
        </keep-alive>
      </section>
    </template>
  </div>
</template>

<script>
import { HOME_TITLE } from "@/config/app";
import { isMjjDeploy } from "@/config/api";
import OnlineCapacity from "@/components/statistics/OnlineCapacity.vue";
import OfflineCapacity from "@/components/statistics/offline/OfflineCapacity.vue";

const TAB_STORAGE_KEY = "home-capacity-active-tab";

const CAPACITY_TABS = [
  {
    name: "online",
    componentName: "OnlineCapacity",
    label: "线上产能 / 预警 / 工作量",
    desc: "订单产能趋势、当前工作量与今日预警一览",
    icon: "el-icon-data-analysis",
    component: OnlineCapacity,
  },
  {
    name: "offline",
    componentName: "OfflineCapacity",
    label: "门店产能 / 预警 / 工作量",
    desc: "门店订单产能趋势、当前工作量与今日预警一览",
    icon: "el-icon-office-building",
    component: OfflineCapacity,
  },
];

export default {
  name: "Home",
  data() {
    const showCapacityDashboard = isMjjDeploy();
    let activeName = CAPACITY_TABS[0].name;
    if (showCapacityDashboard) {
      try {
        const cached = sessionStorage.getItem(TAB_STORAGE_KEY);
        if (cached && CAPACITY_TABS.some((t) => t.name === cached)) {
          activeName = cached;
        }
      } catch (e) {
        // ignore
      }
    }
    return {
      homeTitle: HOME_TITLE,
      showCapacityDashboard,
      capacityTabs: CAPACITY_TABS,
      activeName,
      isRefreshing: false,
    };
  },
  computed: {
    currentTab() {
      return (
        this.capacityTabs.find((t) => t.name === this.activeName) ||
        this.capacityTabs[0]
      );
    },
    capacityComponentNames() {
      return this.capacityTabs.map((t) => t.componentName);
    },
  },
  methods: {
    switchTab(name) {
      this.activeName = name;
      try {
        sessionStorage.setItem(TAB_STORAGE_KEY, name);
      } catch (e) {
        // ignore
      }
    },
    async refreshCurrentTab() {
      const panel = this.$refs.activePanel;
      if (!panel) {
        return;
      }
      this.isRefreshing = true;
      try {
        if (typeof panel.refresh === "function") {
          await panel.refresh();
        } else if (typeof panel.getData === "function") {
          await panel.getData();
        } else if (typeof panel.init === "function") {
          await panel.init();
        }
      } finally {
        this.isRefreshing = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.home {
  min-height: calc(100vh - 111px);
}

.home-welcome {
  height: calc(100vh - 111px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.home--dashboard {
  padding: 16px 20px 24px;
  box-sizing: border-box;
}

.home-dash__hero {
  margin-bottom: 16px;
}

.home-dash__title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 600;
  color: #303133;
}

.home-dash__desc {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

.home-dash__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.home-dash__nav-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #409eff;
    color: #409eff;
  }

  &.is-active {
    border-color: #409eff;
    background: #ecf5ff;
    color: #409eff;
  }
}

.home-dash__nav-icon {
  font-size: 14px;
}

.home-dash__panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.home-dash__panel-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.home-dash__panel-desc {
  margin: 0;
  font-size: 12px;
  color: #909399;
}

.home-dash__body {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
</style>
